import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import ShopLogo from "../images/shop_logo.png";
import { SHOP_NAME } from "../Variables.js";
import '../styles/BalanceSheet.css';

Modal.setAppElement('#root');

const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const sortByCode = (a, b) => {
  const codeA = (a.code || '').toString();
  const codeB = (b.code || '').toString();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

const categorizeAccounts = (accounts, balances) => {
  const categories = {
    currentAssets: [],
    nonCurrentAssets: [],
    currentLiabilities: [],
    nonCurrentLiabilities: [],
    equity: []
  };

  accounts.forEach(account => {
    if (!account.active) return;
    const balance = balances[account.id] || 0;
    if (Math.abs(balance) < 0.01) return;
    if (account.financialStatement !== "Balance Sheet") return;

    const type = (account.type || '').toLowerCase();
    const name = (account.name || '').toLowerCase();
    const code = (account.code || '').toString();

    const accountData = { ...account, balance: Math.abs(balance) };

    if (type === 'asset') {
      const isCurrent =
        name.includes('cash') || name.includes('bank') || name.includes('receivable') ||
        name.includes('inventory') || name.includes('prepaid') || name.includes('deposit') ||
        ['1', '10', '11', '12'].some(p => code.startsWith(p));

      if (isCurrent) categories.currentAssets.push(accountData);
      else categories.nonCurrentAssets.push(accountData);
    }
    else if (type === 'liability') {
      const isCurrent =
        name.includes('payable') || name.includes('accrued') || name.includes('tax') ||
        name.includes('short') || name.includes('current') ||
        ['2', '20', '21'].some(p => code.startsWith(p));

      if (isCurrent) categories.currentLiabilities.push(accountData);
      else categories.nonCurrentLiabilities.push(accountData);
    }
    else if (type === 'equity') {
      categories.equity.push(accountData);
    }
  });

  Object.keys(categories).forEach(key => categories[key].sort(sortByCode));
  return categories;
};

const BalanceSheet = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [isFetching, setIsFetching] = useState(false);

  const accountsCollectionRef = collection(db, "accounts");
  const journalEntriesCollectionRef = collection(db, "journalEntries");

  useEffect(() => {
    if (!user || !userData) {
      setLoading(true);
      return;
    }
    setLoading(false);
    fetchAccounts();
  }, [user, userData]);

  useEffect(() => {
    if (accounts.length > 0) generateBalanceSheet();
  }, [asOfDate, accounts]);

  const fetchAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef);
      const data = await getDocs(q);
      setAccounts(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (err) {
      setError("Failed to fetch accounts");
      setShowModal(true);
    }
  };

  const fetchJournalEntries = async () => {
    if (!user?.uid) return [];
    try {
      const q = query(journalEntriesCollectionRef);
      const data = await getDocs(q);
      return data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    } catch (err) {
      setError("Failed to fetch journal entries");
      setShowModal(true);
      return [];
    }
  };

  const calculateAccountBalance = (accountId, accountName, entries) => {
    let balance = 0;
    entries.forEach(entry => {
      if (new Date(entry.date) <= new Date(asOfDate)) {
        entry.lines.forEach(line => {
          if (line.accountName === accountName || (line.accountId && line.accountId === accountId)) {
            balance += (Number(line.debit) || 0) - (Number(line.credit) || 0);
          }
        });
      }
    });
    return balance;
  };

  const generateBalanceSheet = async () => {
    if (accounts.length === 0) return;
    setIsFetching(true);
    try {
      const entries = await fetchJournalEntries();
      const balances = {};
      accounts.forEach(acc => {
        balances[acc.id] = calculateAccountBalance(acc.id, acc.name, entries);
      });

      const categorized = categorizeAccounts(accounts, balances);

      const totalCurrentAssets = categorized.currentAssets.reduce((s, a) => s + a.balance, 0);
      const totalNonCurrentAssets = categorized.nonCurrentAssets.reduce((s, a) => s + a.balance, 0);
      const totalAssets = totalCurrentAssets + totalNonCurrentAssets;

      const totalCurrentLiabilities = categorized.currentLiabilities.reduce((s, l) => s + l.balance, 0);
      const totalNonCurrentLiabilities = categorized.nonCurrentLiabilities.reduce((s, l) => s + l.balance, 0);
      const totalLiabilities = totalCurrentLiabilities + totalNonCurrentLiabilities;

      let totalEquity = categorized.equity.reduce((s, e) => s + e.balance, 0);

      const hasRetained = categorized.equity.some(e => e.name.toLowerCase().includes('retained'));
      const retainedEarnings = totalAssets - totalLiabilities - totalEquity;

      if (!hasRetained && Math.abs(retainedEarnings) > 0.01) {
        categorized.equity.push({
          id: 'retained',
          name: 'Retained Earnings (Calculated)',
          code: 'RE',
          balance: retainedEarnings,
          type: 'Equity',
          financialStatement: 'Balance Sheet'
        });
      }

      const finalTotalEquity = categorized.equity.reduce((s, e) => s + e.balance, 0);
      const totalLiabilitiesAndEquity = totalLiabilities + finalTotalEquity;
      const isBalanced = Math.abs(totalAssets - totalLiabilitiesAndEquity) < 0.01;

      setBalanceSheetData({
        categorizedAccounts: categorized,
        totals: {
          totalCurrentAssets,
          totalNonCurrentAssets,
          totalAssets,
          totalCurrentLiabilities,
          totalNonCurrentLiabilities,
          totalLiabilities,
          totalEquity: finalTotalEquity,
          totalLiabilitiesAndEquity,
          isBalanced
        }
      });
    } catch (err) {
      setError("Failed to generate balance sheet");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  // EXACTLY YOUR ORIGINAL PDF STYLING
  const generatePDF = () => {
    if (!balanceSheetData) {
      setError("No balance sheet data available");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const { categorizedAccounts, totals } = balanceSheetData;
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let currentY = 10;
    try {
      doc.addImage(ShopLogo, "PNG", 14, currentY, 15, 15);
    } catch (err) {
      console.log("Logo not loaded for PDF");
    }
    
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(SHOP_NAME, 35, currentY + 5);
    
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("Balance Sheet", 35, currentY + 13);
    
    doc.setFontSize(10);    
    doc.text(`Generated by: ${fullName}`, 35, currentY + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, currentY + 23);
    doc.text(`As of ${dateFormatter.format(new Date(asOfDate))}`, 35, currentY + 28);

    currentY += 35;

    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const spacing = 10;
    const availableWidth = pageWidth - (2 * margin) - spacing;
    const tableWidth = availableWidth / 2;

    const assetsTableData = [];
    const liabilitiesEquityTableData = [];

    // === ASSETS ===
    if (categorizedAccounts.currentAssets.length > 0) {
      assetsTableData.push([{ content: 'CURRENT ASSETS', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } }]);
      categorizedAccounts.currentAssets.forEach(asset => {
        assetsTableData.push([
          `  ${asset.name}`,
          { content: `N${asset.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { halign: 'right' } }
        ]);
      });
      assetsTableData.push([
        { content: 'Total Current Assets', styles: { fontStyle: 'bold' } },
        { content: `N${totals.totalCurrentAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', halign: 'right' } }
      ]);
      assetsTableData.push([{ content: '', colSpan: 2, styles: { fillColor: [255, 255, 255] } }]);
    }

    if (categorizedAccounts.nonCurrentAssets.length > 0) {
      assetsTableData.push([{ content: 'NON-CURRENT ASSETS', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } }]);
      categorizedAccounts.nonCurrentAssets.forEach(asset => {
        assetsTableData.push([
          `  ${asset.name}`,
          { content: `N${asset.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { halign: 'right' } }
        ]);
      });
      assetsTableData.push([
        { content: 'Total Non-Current Assets', styles: { fontStyle: 'bold' } },
        { content: `N${totals.totalNonCurrentAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', halign: 'right' } }
      ]);
    }

    assetsTableData.push([
      { content: 'TOTAL ASSETS', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [200, 200, 200] } }
    ]);
    assetsTableData.push([
      { content: 'TOTAL ASSETS', styles: { fontStyle: 'bold' } },
      { content: `N${totals.totalAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', halign: 'right' } }
    ]);

    // === LIABILITIES & EQUITY ===
    if (categorizedAccounts.currentLiabilities.length > 0) {
      liabilitiesEquityTableData.push([{ content: 'CURRENT LIABILITIES', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } }]);
      categorizedAccounts.currentLiabilities.forEach(liability => {
        liabilitiesEquityTableData.push([
          `  ${liability.name}`,
          { content: `N${liability.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { halign: 'right' } }
        ]);
      });
      liabilitiesEquityTableData.push([
        { content: 'Total Current Liabilities', styles: { fontStyle: 'bold' } },
        { content: `N${totals.totalCurrentLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', halign: 'right' } }
      ]);
      liabilitiesEquityTableData.push([{ content: '', colSpan: 2, styles: { fillColor: [255, 255, 255] } }]);
    }

    if (categorizedAccounts.nonCurrentLiabilities.length > 0) {
      liabilitiesEquityTableData.push([{ content: 'LONG-TERM LIABILITIES', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } }]);
      categorizedAccounts.nonCurrentLiabilities.forEach(liability => {
        liabilitiesEquityTableData.push([
          `  ${liability.name}`,
          { content: `N${liability.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { halign: 'right' } }
        ]);
      });
      liabilitiesEquityTableData.push([
        { content: 'Total Long-term Liabilities', styles: { fontStyle: 'bold' } },
        { content: `N${totals.totalNonCurrentLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', halign: 'right' } }
      ]);
      liabilitiesEquityTableData.push([{ content: '', colSpan: 2, styles: { fillColor: [255, 255, 255] } }]);
    }

    liabilitiesEquityTableData.push([
      { content: 'TOTAL LIABILITIES', styles: { fontStyle: 'bold' } },
      { content: `N${totals.totalLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', halign: 'right' } }
    ]);
    liabilitiesEquityTableData.push([{ content: '', colSpan: 2, styles: { fillColor: [255, 255, 255] } }]);

    if (categorizedAccounts.equity.length > 0) {
      liabilitiesEquityTableData.push([{ content: 'EQUITY', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } }]);
      categorizedAccounts.equity.forEach(equity => {
        liabilitiesEquityTableData.push([
          `  ${equity.name}`,
          { content: `N${equity.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { halign: 'right' } }
        ]);
      });
      liabilitiesEquityTableData.push([
        { content: 'TOTAL EQUITY', styles: { fontStyle: 'bold' } },
        { content: `N${totals.totalEquity.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', halign: 'right' } }
      ]);
    }

    liabilitiesEquityTableData.push([
      { content: 'TOTAL LIABILITIES & EQUITY', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [200, 200, 200] } }
    ]);
    liabilitiesEquityTableData.push([
      { content: 'TOTAL LIABILITIES & EQUITY', styles: { fontStyle: 'bold' } },
      { content: `N${totals.totalLiabilitiesAndEquity.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', halign: 'right' } }
    ]);

    const tableColumns = [
      { header: 'Account', dataKey: 'account' },
      { header: 'Amount', dataKey: 'amount' }
    ];

    autoTable(doc, {
      startY: currentY,
      head: [[{ content: 'ASSETS', colSpan: 2, styles: { fontStyle: 'bold', fontSize: 14, halign: 'center', fillColor: [41, 128, 185], textColor: 255 } }]],
      body: assetsTableData,
      columns: tableColumns,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3, textColor: 0 },
      columnStyles: {
        0: { cellWidth: 'auto', fontStyle: 'normal' },
        1: { cellWidth: 40, halign: 'right', fontStyle: 'normal' }
      },
      margin: { left: margin },
      tableWidth: tableWidth
    });

    const rightTableStartX = margin + tableWidth + spacing;

    autoTable(doc, {
      startY: currentY,
      head: [[{ content: 'LIABILITIES & EQUITY', colSpan: 2, styles: { fontStyle: 'bold', fontSize: 14, halign: 'center', fillColor: [41, 128, 185], textColor: 255 } }]],
      body: liabilitiesEquityTableData,
      columns: tableColumns,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3, textColor: 0 },
      columnStyles: {
        0: { cellWidth: 'auto', fontStyle: 'normal' },
        1: { cellWidth: 40, halign: 'right', fontStyle: 'normal' }
      },
      margin: { left: rightTableStartX },
      tableWidth: tableWidth
    });

    const finalY = Math.max(doc.lastAutoTable.finalY || 0) + 30;
    
    doc.setFontSize(10);
    doc.setTextColor(
      totals.isBalanced ? 0 : 255,
      totals.isBalanced ? 128 : 0,
      0
    );
    doc.setFont("helvetica", "bold");
    doc.text(`Status: ${totals.isBalanced ? "BALANCED" : "OUT OF BALANCE"}`, margin, finalY);
    doc.setTextColor(0, 0, 0);

    doc.save(`BalanceSheet_${asOfDate}.pdf`);
  };

  const refreshBalanceSheet = () => {
    generateBalanceSheet();
    setSuccessMessage("Balance Sheet refreshed successfully");
    setShowModal(true);
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme'];

  return (
    <>
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Balance Sheet</h2>
            <Link to="#" className="home-link" onClick={refreshBalanceSheet}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">
            <div className="row gap-15 align-center justify-center" style={{ marginTop: '10px'}}>
              <div className="row gap-1 align-center">
                <input
                  type="date"
                  className="input-field"
                  value={asOfDate}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  style={{ marginBottom: '0px' }}
                />
              </div>
              <button 
                className="login-button login-button-d caps" 
                onClick={generatePDF}
                disabled={!balanceSheetData}
                style={{ marginTop: '0px' }}
              >
                DOWNLOAD
              </button>
            </div>

            {isFetching && (
              <div className="loading-indicator" style={{ textAlign: 'center', padding: '40px' }}>
                <Loader text={"GENERATING BALANCE SHEET"} />
                <p style={{ marginTop: '15px', color: '#666' }}>
                  Calculating account balances and categorizing transactions...
                </p>
              </div>
            )}

            {!isFetching && !balanceSheetData && accounts.length > 0 && (
              <div className="no-data-message" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <p>No balance sheet data available. Click the refresh button to generate.</p>
              </div>
            )}

            {!isFetching && accounts.length === 0 && (
              <div className="no-accounts-message" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <p>No accounts found. Please create accounts first.</p>
              </div>
            )}

            {!isFetching && balanceSheetData && (
              <>
                {/* Your Original Top Summary - Fully Restored */}
                <div className={`balance-status ${balanceSheetData.totals.isBalanced ? 'balanced' : 'unbalanced'}`} style={{maxWidth: '800px'}}>
                  <div className="row align-center gap-1">
                    <i className={`fas ${balanceSheetData.totals.isBalanced ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                    <span className="bold">
                      {balanceSheetData.totals.isBalanced ? 'BALANCED' : 'OUT OF BALANCE'}
                    </span>
                  </div>
                  <div className="row gap-1 totals-row">
                    <span>Total Assets: <strong>N{balanceSheetData.totals.totalAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></span>
                    <span>Total Liabilities & Equity: <strong>N{balanceSheetData.totals.totalLiabilitiesAndEquity.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></span>
                    <span>Difference: <strong>N{Math.abs(balanceSheetData.totals.totalAssets - balanceSheetData.totals.totalLiabilitiesAndEquity).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></span>
                  </div>
                </div>

                {/* Your Original On-Screen Layout */}
                <div className="balance-sheet-container scroll-fixed scroll-fixed6">
                  <div className="balance-sheet-column">
                    <h3 className="balance-sheet-header">ASSETS</h3>
                    
                    {balanceSheetData.categorizedAccounts.currentAssets.length > 0 && (
                      <div className="balance-sheet-section">
                        <h4 className="balance-sheet-subheader">Current Assets</h4>
                        {balanceSheetData.categorizedAccounts.currentAssets.map(asset => (
                          <div key={asset.id} className="balance-sheet-item row space-between">
                            <span>{asset.name}</span>
                            <span className="amount">N{asset.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        <div className="balance-sheet-total row space-between">
                          <strong>Total Current Assets</strong>
                          <strong className="amount">N{balanceSheetData.totals.totalCurrentAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      </div>
                    )}

                    {balanceSheetData.categorizedAccounts.nonCurrentAssets.length > 0 && (
                      <div className="balance-sheet-section">
                        <h4 className="balance-sheet-subheader">Non-Current Assets</h4>
                        {balanceSheetData.categorizedAccounts.nonCurrentAssets.map(asset => (
                          <div key={asset.id} className="balance-sheet-item row space-between">
                            <span>{asset.name}</span>
                            <span className="amount">N{asset.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        <div className="balance-sheet-total row space-between">
                          <strong>Total Non-Current Assets</strong>
                          <strong className="amount">N{balanceSheetData.totals.totalNonCurrentAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      </div>
                    )}

                    <div className="balance-sheet-grand-total row space-between">
                      <strong>TOTAL ASSETS</strong>
                      <strong className="amount">N{balanceSheetData.totals.totalAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>
                  </div>

                  <div className="balance-sheet-column">
                    <h3 className="balance-sheet-header">LIABILITIES & EQUITY</h3>
                    
                    {balanceSheetData.categorizedAccounts.currentLiabilities.length > 0 && (
                      <div className="balance-sheet-section">
                        <h4 className="balance-sheet-subheader">Current Liabilities</h4>
                        {balanceSheetData.categorizedAccounts.currentLiabilities.map(liability => (
                          <div key={liability.id} className="balance-sheet-item row space-between">
                            <span>{liability.name}</span>
                            <span className="amount">N{liability.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        <div className="balance-sheet-total row space-between">
                          <strong>Total Current Liabilities</strong>
                          <strong className="amount">N{balanceSheetData.totals.totalCurrentLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      </div>
                    )}

                    {balanceSheetData.categorizedAccounts.nonCurrentLiabilities.length > 0 && (
                      <div className="balance-sheet-section">
                        <h4 className="balance-sheet-subheader">Long-term Liabilities</h4>
                        {balanceSheetData.categorizedAccounts.nonCurrentLiabilities.map(liability => (
                          <div key={liability.id} className="balance-sheet-item row space-between">
                            <span>{liability.name}</span>
                            <span className="amount">N{liability.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        <div className="balance-sheet-total row space-between">
                          <strong>Total Long-term Liabilities</strong>
                          <strong className="amount">N{balanceSheetData.totals.totalNonCurrentLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      </div>
                    )}

                    <div className="balance-sheet-total row space-between">
                      <strong>TOTAL LIABILITIES</strong>
                      <strong className="amount">N{balanceSheetData.totals.totalLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>

                    <div className="balance-sheet-section">
                      <h4 className="balance-sheet-subheader">Equity</h4>
                      {balanceSheetData.categorizedAccounts.equity.map(equity => (
                        <div key={equity.id || equity.name} className="balance-sheet-item row space-between">
                          <span>{equity.name}</span>
                          <span className="amount">N{equity.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                      <div className="balance-sheet-total row space-between">
                        <strong>TOTAL EQUITY</strong>
                        <strong className="amount">N{balanceSheetData.totals.totalEquity.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    </div>

                    <div className="balance-sheet-grand-total row space-between">
                      <strong>TOTAL LIABILITIES & EQUITY</strong>
                      <strong className="amount">N{balanceSheetData.totals.totalLiabilitiesAndEquity.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {showModal && (
            <SuccessModal
              icon={error ? "xmark" : "check"}
              iconColor={error ? "red" : "green"}
              message={error || successMessage}
              onClose={() => {
                setError("");
                setSuccessMessage("");
                setShowModal(false);
              }}
            />
          )}
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
    </>
  );
};

export default BalanceSheet;